// Route configuration.
//
// Route tree:
//   /                 → Public landing page (marketing)
//   /login            → auth pages (redirect to /app if logged in)
//   /forgot-password  → auth pages (redirect to /app if logged in)
//   /reset-password   → auth pages (redirect to /app if logged in)
//   /app              → Dashboard (inside AppLayout with sidebar)
//   /app/engagements  → Engagements list
//   /app/profile      → Profile
//   ...

import { Navigate } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";
import { AdminPage } from "@/pages/AdminPage";
import { AppLayout } from "@/components/layout/AppLayout";
import { BlogIndexPage } from "@/pages/BlogIndexPage";
import { BlogPostPage } from "@/pages/BlogPostPage";
import { RootLayout } from "@/components/RootLayout";
import { DashboardPage } from "@/pages/DashboardPage";
import { EngagementDetailPage } from "@/pages/EngagementDetailPage";
import { EngagementsPage } from "@/pages/EngagementsPage";
import {
  NotFoundPage,
  RouteErrorPage,
  ServiceUnavailablePage,
} from "@/pages/ErrorPages";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { LandingPage } from "@/pages/LandingPage";
import { PlatformPage } from "@/pages/PlatformPage";
import { PricingPage } from "@/pages/PricingPage";
import { DesignSystemPage } from "@/pages/DesignSystemPage";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { localePath } from "@/i18n/config";
import { LoginPage } from "@/pages/LoginPage";
import { NewEngagementPage } from "@/pages/NewEngagementPage";
import { PrivacyPage } from "@/pages/PrivacyPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ScanHistoryPage } from "@/pages/ScanHistoryPage";
import { TermsPage } from "@/pages/TermsPage";
import { UpgradePage } from "@/pages/UpgradePage";
import { VerifyEmailPage } from "@/pages/VerifyEmailPage";
import { VerifyEmailRequiredPage } from "@/pages/VerifyEmailRequiredPage";
import { useAuthStore } from "@/stores/authStore";
import type { ReactNode } from "react";

function RequireAuth({ children }: { children: ReactNode }) {
  const hydrated = useAuthStore((s) => s.hydrated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  if (!hydrated) return null;
  if (!accessToken) return <Navigate to="/login" replace />;
  // Email verification gate — server-side enforcement exists for
  // billable actions (scan execution, etc.) but the whole shell is
  // also blocked client-side so an unverified user can't even browse
  // /app and discover features they can't use. The user has refresh
  // tokens but cannot reach any /app route until they verify.
  if (user && !user.email_verified) {
    return <Navigate to="/verify-email-required" replace state={{ email: user.email }} />;
  }
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const hydrated = useAuthStore((s) => s.hydrated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  if (!hydrated) return null;
  // Same logic mirrored: an authed-but-unverified user that lands on
  // /login (e.g. after clicking "back to sign in" from the
  // verify-required page) gets routed to verification, NOT to /app.
  // Otherwise they'd loop /app → RequireAuth → /verify-email-required.
  if (accessToken && user && !user.email_verified) {
    return <Navigate to="/verify-email-required" replace state={{ email: user.email }} />;
  }
  if (accessToken) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// All routes nest under a single root that supplies the on-brand
// `errorElement` — any uncaught render error or thrown route Response
// (e.g. a 404 or 503 from a future loader) lands on RouteErrorPage
// instead of React Router's developer screen. RootLayout also mounts
// the API health monitor that auto-redirects to /503 when the backend
// goes silent (e.g. mid-deploy).
export const routes: RouteRecord[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      // ── Public ────────────────────────────────────────────────────
      // Landing is localized: pt at `/`, en at `/en`, es at `/es`. Each
      // is pre-rendered with its own LocaleProvider + hreflang. Legal
      // pages aren't localized yet (separate pass).
      {
        path: "/",
        element: (
          <LocaleProvider locale="pt">
            <LandingPage />
          </LocaleProvider>
        ),
      },
      {
        path: "/en",
        element: (
          <LocaleProvider locale="en">
            <LandingPage />
          </LocaleProvider>
        ),
      },
      {
        path: "/es",
        element: (
          <LocaleProvider locale="es">
            <LandingPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("pt", "platform"),
        element: (
          <LocaleProvider locale="pt">
            <PlatformPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("en", "platform"),
        element: (
          <LocaleProvider locale="en">
            <PlatformPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("es", "platform"),
        element: (
          <LocaleProvider locale="es">
            <PlatformPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("pt", "pricing"),
        element: (
          <LocaleProvider locale="pt">
            <PricingPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("en", "pricing"),
        element: (
          <LocaleProvider locale="en">
            <PricingPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("es", "pricing"),
        element: (
          <LocaleProvider locale="es">
            <PricingPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("pt", "privacy"),
        element: (
          <LocaleProvider locale="pt">
            <PrivacyPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("en", "privacy"),
        element: (
          <LocaleProvider locale="en">
            <PrivacyPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("es", "privacy"),
        element: (
          <LocaleProvider locale="es">
            <PrivacyPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("pt", "terms"),
        element: (
          <LocaleProvider locale="pt">
            <TermsPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("en", "terms"),
        element: (
          <LocaleProvider locale="en">
            <TermsPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("es", "terms"),
        element: (
          <LocaleProvider locale="es">
            <TermsPage />
          </LocaleProvider>
        ),
      },
      // ── Blog (public, dynamic — NOT pre-rendered by SSG) ──────────
      // Index + post per locale. The "blog" slug is the same across
      // locales; only the /<locale> prefix differs. Per-post SEO <head>
      // is injected server-side by the FastAPI catchall.
      {
        path: localePath("pt", "blog"),
        element: (
          <LocaleProvider locale="pt">
            <BlogIndexPage />
          </LocaleProvider>
        ),
      },
      {
        path: `${localePath("pt", "blog")}/:slug`,
        element: (
          <LocaleProvider locale="pt">
            <BlogPostPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("en", "blog"),
        element: (
          <LocaleProvider locale="en">
            <BlogIndexPage />
          </LocaleProvider>
        ),
      },
      {
        path: `${localePath("en", "blog")}/:slug`,
        element: (
          <LocaleProvider locale="en">
            <BlogPostPage />
          </LocaleProvider>
        ),
      },
      {
        path: localePath("es", "blog"),
        element: (
          <LocaleProvider locale="es">
            <BlogIndexPage />
          </LocaleProvider>
        ),
      },
      {
        path: `${localePath("es", "blog")}/:slug`,
        element: (
          <LocaleProvider locale="es">
            <BlogPostPage />
          </LocaleProvider>
        ),
      },

      // Legacy redirects: the marketing pages first shipped with English
      // slugs; now slugs are localized. Keep old URLs working (pt + es).
      { path: "/platform", element: <Navigate to={localePath("pt", "platform")} replace /> },
      { path: "/pricing", element: <Navigate to={localePath("pt", "pricing")} replace /> },
      { path: "/privacy", element: <Navigate to={localePath("pt", "privacy")} replace /> },
      { path: "/terms", element: <Navigate to={localePath("pt", "terms")} replace /> },
      { path: "/es/platform", element: <Navigate to={localePath("es", "platform")} replace /> },
      { path: "/es/pricing", element: <Navigate to={localePath("es", "pricing")} replace /> },
      { path: "/es/privacy", element: <Navigate to={localePath("es", "privacy")} replace /> },
      { path: "/es/terms", element: <Navigate to={localePath("es", "terms")} replace /> },

      // Internal living style guide — noindex, not pre-rendered (served
      // client-side via the SPA fallback), no locale wrapper.
      { path: "/design-system", element: <DesignSystemPage /> },

      // ── Auth (accessible only to logged-out users) ────────────────
      {
        path: "/login",
        element: (
          <RedirectIfAuthed>
            <LoginPage />
          </RedirectIfAuthed>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <RedirectIfAuthed>
            <ForgotPasswordPage />
          </RedirectIfAuthed>
        ),
      },
      {
        path: "/reset-password",
        element: (
          <RedirectIfAuthed>
            <ResetPasswordPage />
          </RedirectIfAuthed>
        ),
      },
      // Email verification — both routes are public so an authed-but-
      // unverified user can still reach them. The verify endpoint also
      // works for an unauthenticated visitor clicking from email.
      { path: "/verify-email", element: <VerifyEmailPage /> },
      { path: "/verify-email-required", element: <VerifyEmailRequiredPage /> },

      // ── Authenticated app (under /app) ───────────────────────────
      {
        path: "/app",
        element: (
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "engagements", element: <EngagementsPage /> },
          { path: "engagements/new", element: <NewEngagementPage /> },
          { path: "engagements/:id", element: <EngagementDetailPage /> },
          { path: "history", element: <ScanHistoryPage /> },
          { path: "profile", element: <ProfilePage /> },
          { path: "upgrade", element: <UpgradePage /> },
          { path: "admin", element: <AdminPage /> },
        ],
      },

      // ── Branded error pages reachable by direct navigation ───────
      // /503 is exposed as a real route so the API layer (or operators)
      // can navigate to it when the backend is unreachable. The 404
      // catch-all replaces the previous silent redirect to /.
      { path: "/503", element: <ServiceUnavailablePage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
